// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { EducBox, ResultCard, InputRow, fmt } from './shared';
import { useDeepLinkParams, hasAllParams, ShareCalcButton, scrollToResult } from './deepLink';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Hardcoded fallback indices (FOI base 2015=100)
const FALLBACK_INDICES = [
  { anno: 2015, label: '2015', foi: 100.0 },
  { anno: 2016, label: '2016', foi: 99.9 },
  { anno: 2017, label: '2017', foi: 101.0 },
  { anno: 2018, label: '2018', foi: 102.7 },
  { anno: 2019, label: '2019', foi: 103.1 },
  { anno: 2020, label: '2020', foi: 102.7 },
  { anno: 2021, label: '2021', foi: 105.8 },
  { anno: 2022, label: '2022', foi: 114.5 },
  { anno: 2023, label: '2023', foi: 119.2 },
  { anno: 2024, label: '2024', foi: 120.4 },
  { anno: 2025, label: '2025', foi: 122.2 },
];

const RivalutazioneISTAT = () => {
  const url = useDeepLinkParams({ importo: 'num', anno_da: 'year' });
  const [indices, setIndices] = useState(FALLBACK_INDICES);
  const [importo, setImporto] = useState(url.importo ?? '10000');
  const [annoPartenza, setAnnoPartenza] = useState(url.anno_da ?? '2020');
  // anno_a defaults to current year (component default per spec)
  const [annoArrivo, setAnnoArrivo] = useState(String(new Date().getFullYear()));

  useEffect(() => {
    fetch(`${API_URL}/api/istat/indices`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data && data.length) setIndices(data); })
      .catch(() => {});
    if (hasAllParams(url, ['importo', 'anno_da'])) scrollToResult('istat-result');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { rivalutato, variazione, percVariazione, valid } = useMemo(() => {
    const I = parseFloat(importo) || 0;
    const from = parseInt(annoPartenza);
    const to = parseInt(annoArrivo);
    if (from >= to) return { rivalutato: 0, variazione: 0, percVariazione: 0, valid: false };
    const idxFrom = indices.find(x => x.anno === from);
    const idxTo = indices.find(x => x.anno === to);
    if (!idxFrom || !idxTo) return { rivalutato: 0, variazione: 0, percVariazione: 0, valid: false };
    const ratio = idxTo.foi / idxFrom.foi;
    const rivalutato = I * ratio;
    const variazione = rivalutato - I;
    const percVariazione = (ratio - 1) * 100;
    return { rivalutato, variazione, percVariazione, valid: true };
  }, [importo, annoPartenza, annoArrivo, indices]);

  const availableYears = indices.map(x => x.anno).sort((a, b) => a - b);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="rivalutazione-istat-calc">
      <EducBox text="La rivalutazione ISTAT permette di calcolare il potere d'acquisto reale di un importo nel tempo, utilizzando gli indici FOI (Famiglie Operai Impiegati) dell'ISTAT. È usata per aggiornare canoni di locazione, TFR, indennità e contratti indicizzati all'inflazione italiana." />

      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <InputRow
          label="Importo originale (€)"
          value={importo}
          onChange={setImporto}
          prefix="€"
          tooltip="Importo da rivalutare in base all'inflazione ISTAT"
        />
        <div>
          <label className="text-[#888780] text-sm block mb-1">Anno di partenza</label>
          <select
            value={annoPartenza}
            onChange={e => setAnnoPartenza(e.target.value)}
            className="w-full bg-[#1C1C1A] border border-white/[0.08] text-[#E8E8E4] rounded-md px-3 py-2 text-sm"
            data-testid="anno-partenza-select"
          >
            {availableYears.filter(y => y < parseInt(annoArrivo)).map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[#888780] text-sm block mb-1">Anno di arrivo</label>
          <select
            value={annoArrivo}
            onChange={e => setAnnoArrivo(e.target.value)}
            className="w-full bg-[#1C1C1A] border border-white/[0.08] text-[#E8E8E4] rounded-md px-3 py-2 text-sm"
            data-testid="anno-arrivo-select"
          >
            {availableYears.filter(y => y > parseInt(annoPartenza)).map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      {valid ? (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6" data-testid="istat-result">
            <ResultCard label="Importo rivalutato" value={`€${fmt(rivalutato)}`} gold />
            <ResultCard label="Variazione assoluta" value={`+€${fmt(variazione)}`} green />
            <ResultCard label="Variazione %FOI" value={`+${percVariazione.toFixed(2)}%`} />
            <ResultCard label="Periodo analizzato" value={`${parseInt(annoArrivo) - parseInt(annoPartenza)} anni`} />
          </div>
          <ShareCalcButton slug="rivalutazione-istat" testid="istat-share-btn"
            getValues={() => ({ importo, anno_da: annoPartenza })} />

          <div className="mb-6 p-4 rounded-xl text-sm" style={{ background: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.2)' }}>
            <p className="text-xs text-[#888780] mb-2 font-medium">Calcolo dettagliato</p>
            <div className="space-y-1 text-[#888780]">
              <p>Indice FOI {annoPartenza}: <span className="text-[#E8E8E4]">{indices.find(x => x.anno === parseInt(annoPartenza))?.foi?.toFixed(1)}</span></p>
              <p>Indice FOI {annoArrivo}: <span className="text-[#E8E8E4]">{indices.find(x => x.anno === parseInt(annoArrivo))?.foi?.toFixed(1)}</span></p>
              <p>Coefficiente di rivalutazione: <span className="text-[#E8E8E4]">{(indices.find(x => x.anno === parseInt(annoArrivo))?.foi / indices.find(x => x.anno === parseInt(annoPartenza))?.foi).toFixed(6)}</span></p>
              <p>€{fmt(parseFloat(importo))} × {(indices.find(x => x.anno === parseInt(annoArrivo))?.foi / indices.find(x => x.anno === parseInt(annoPartenza))?.foi).toFixed(4)} = <strong className="text-[#C9A84C]">€{fmt(rivalutato)}</strong></p>
            </div>
          </div>

          {/* Tabella storica */}
          <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
            <div className="p-3 text-xs font-medium text-[#888780]" style={{ background: 'rgba(255,255,255,0.03)' }}>
              Indici FOI storici (base 2015=100)
            </div>
            <div className="overflow-x-auto max-h-56 overflow-y-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-white/[0.08]">
                    <th className="py-2 px-3 text-left text-[#888780]">Anno</th>
                    <th className="py-2 px-3 text-right text-[#888780]">Indice FOI</th>
                    <th className="py-2 px-3 text-right text-[#888780]">Var. annua</th>
                    <th className="py-2 px-3 text-right text-[#888780]">€{fmt(parseFloat(importo))} rivalutato</th>
                  </tr>
                </thead>
                <tbody>
                  {indices.filter(x => x.anno >= parseInt(annoPartenza) && x.anno <= parseInt(annoArrivo)).map((idx, i, arr) => {
                    const prev = arr[i - 1];
                    const varAnn = prev ? ((idx.foi - prev.foi) / prev.foi * 100).toFixed(2) : '—';
                    const fromIdx = indices.find(x => x.anno === parseInt(annoPartenza));
                    const rivalutatoAnno = fromIdx ? (parseFloat(importo) * idx.foi / fromIdx.foi) : 0;
                    return (
                      <tr key={idx.anno} className="border-b border-white/[0.04]" style={{ background: idx.anno === parseInt(annoArrivo) ? 'rgba(201,168,76,0.05)' : '' }}>
                        <td className="py-1.5 px-3 text-[#888780] font-medium">{idx.anno}</td>
                        <td className="py-1.5 px-3 text-right text-[#E8E8E4]">{idx.foi.toFixed(1)}</td>
                        <td className="py-1.5 px-3 text-right" style={{ color: prev && idx.foi > prev.foi ? '#C9A84C' : '#888780' }}>
                          {varAnn !== '—' ? `+${varAnn}%` : '—'}
                        </td>
                        <td className="py-1.5 px-3 text-right font-medium" style={{ color: idx.anno === parseInt(annoArrivo) ? '#C9A84C' : '#888780' }}>
                          €{fmt(rivalutatoAnno)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <div className="p-8 rounded-xl text-center text-[#888780]" style={{ background: '#252523' }}>
          Seleziona un anno di partenza precedente all'anno di arrivo.
        </div>
      )}

      <p className="text-xs text-[#888780] mt-4 leading-relaxed" style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 12 }}>
        Dati ISTAT FOI (Indice Prezzi al Consumo per Famiglie di Operai e Impiegati, senza tabacchi). Base 2015=100. Fonte: ISTAT. I valori 2024-2025 sono stime. Per rivalutazioni contrattuali/legali consultare il dato ufficiale ISTAT aggiornato.
      </p>
</motion.div>
  );
};

export default RivalutazioneISTAT;
